import React from "react";
import { Progress, Spin, Typography } from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  LoadingOutlined,
} from "@ant-design/icons";
import type { CiCheckPhase, CiDualCheckRunState } from "./ciDualCheckRunState";
import { isCiDualCheckActive } from "./ciDualCheckRunState";

const { Text } = Typography;

const DONE_GREEN = "#52c41a";
const ERROR_RED = "#ff4d4f";
const RUNNING_BLUE = "#1677ff";

const phasePercent = (phase: CiCheckPhase): number => {
  switch (phase) {
    case "done":
      return 100;
    case "running":
      return 55;
    case "pending":
      return 8;
    case "error":
      return 100;
    default:
      return 0;
  }
};

const phaseStrokeColor = (phase: CiCheckPhase): string => {
  if (phase === "done") return DONE_GREEN;
  if (phase === "error") return ERROR_RED;
  if (phase === "running" || phase === "pending") return RUNNING_BLUE;
  return "#d9d9d9";
};

const PhaseIcon: React.FC<{ phase: CiCheckPhase }> = ({ phase }) => {
  if (phase === "done") {
    return <CheckCircleFilled style={{ color: DONE_GREEN, fontSize: 12 }} />;
  }
  if (phase === "error") {
    return <CloseCircleFilled style={{ color: ERROR_RED, fontSize: 12 }} />;
  }
  if (phase === "running") {
    return <LoadingOutlined spin style={{ color: RUNNING_BLUE, fontSize: 12 }} />;
  }
  if (phase === "pending") {
    return <Spin size="small" />;
  }
  return null;
};

const CheckProgressRow: React.FC<{
  label: string;
  phase: CiCheckPhase;
  error?: string;
}> = ({ label, phase, error }) => {
  if (phase === "idle") return null;

  const statusText =
    phase === "done"
      ? "Complete"
      : phase === "running"
        ? "Running…"
        : phase === "pending"
          ? "Waiting…"
          : phase === "error"
            ? error ?? "Failed"
            : "";

  return (
    <div style={{ marginTop: 4 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 6,
          marginBottom: 2,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
          <PhaseIcon phase={phase} />
          <Text style={{ fontSize: 10, lineHeight: 1.2 }}>{label}</Text>
        </div>
        <Text
          type={phase === "error" ? "danger" : "secondary"}
          style={{ fontSize: 9, lineHeight: 1.2, flexShrink: 0 }}
        >
          {statusText}
        </Text>
      </div>
      <Progress
        percent={phasePercent(phase)}
        size="small"
        showInfo={false}
        strokeColor={phaseStrokeColor(phase)}
        strokeWidth={3}
        status={phase === "error" ? "exception" : undefined}
      />
    </div>
  );
};

export interface CommercialInvoiceDualCheckProgressProps {
  runState: CiDualCheckRunState;
}

/** Two progress rows for automatic + AI CI checks while they run on General tab. */
export const CommercialInvoiceDualCheckProgress: React.FC<
  CommercialInvoiceDualCheckProgressProps
> = ({ runState }) => {
  if (!isCiDualCheckActive(runState)) return null;

  return (
    <div
      style={{
        marginBottom: 6,
        padding: "4px 6px",
        borderRadius: 4,
        background: "#fafafa",
        border: "1px solid #f0f0f0",
      }}
    >
      <Text type="secondary" style={{ fontSize: 9, display: "block", lineHeight: 1.2 }}>
        Commercial invoice checks
      </Text>
      <CheckProgressRow
        label="Automatic check"
        phase={runState.heuristic}
        error={runState.heuristicError}
      />
      <CheckProgressRow
        label="AI deep check"
        phase={runState.deepCheck}
        error={runState.deepCheckError}
      />
    </div>
  );
};
